import contentDisposition from 'content-disposition'
import Router from 'koa-router'

import { User, setAccessTokenCookie } from '@things-factory/auth-base'
import { getRepository } from '@things-factory/shell'

import { fonts } from '../controllers/fonts.js'
import { headlessModel } from '../controllers/headless-model.js'
import { headlessPlaylist } from '../controllers/headless-playlist.js'
import { labelcommand } from '../controllers/label-command.js'
import { pdf } from '../controllers/pdf.js'
import { screenshot } from '../controllers/screenshot.js'
import { Board } from '../service/board/board.js'
import { BoardTemplate } from '../service/board-template/board-template.js'

export const standaloneBoardServiceRouter = new Router()

function parseQuery(query) {
  for (const key in query) {
    if (query.hasOwnProperty(key)) {
      try {
        query[key] = JSON.parse(query[key])
      } catch (error) {
        // do nothing
      }
    }
  }

  return query
}

// for board headless-full
standaloneBoardServiceRouter.get('/headless-full/:id', async (context, next) => {
  const { domain, user } = context.state

  if (!(await User.hasPrivilege('query', 'board', domain, user))) {
    context.status = 403
    return
  }

  const { id } = context.params

  const { model, base } = await headlessModel({ domain, id })
  const [fontsToUse, fontStyles] = await fonts(domain)

  model.fonts = fontsToUse
  model.fontStyles = fontStyles

  setAccessTokenCookie(context, await user.sign({ domain }))

  const data = parseQuery({ ...context.query })
  delete data.access_token

  await context.render('internal-board-full-feature-view', { id, model, data, base })
})

// for board headless-full-by-name
standaloneBoardServiceRouter.get('/headless-full-by-name/:name', async (context, next) => {
  const { domain, user } = context.state

  if (!(await User.hasPrivilege('query', 'board', domain, user))) {
    context.status = 403
    return
  }

  const { name } = context.params

  const { model, base, board } = await headlessModel({ domain, name })
  const [fontsToUse, fontStyles] = await fonts(domain)

  model.fonts = fontsToUse
  model.fontStyles = fontStyles

  setAccessTokenCookie(context, await user.sign({ domain }))

  const data = parseQuery({ ...context.query })
  delete data.access_token

  await context.render('internal-board-full-feature-view', { id: board?.id, model, data, base })
})

// for board headless
standaloneBoardServiceRouter.get('/headless/:id', async (context, next) => {
  const { domain, user } = context.state

  if (!(await User.hasPrivilege('query', 'board', domain, user))) {
    context.status = 403
    return
  }

  const { id } = context.params

  const { model, base } = await headlessModel({ domain, id })
  const [fontsToUse, fontStyles] = await fonts(domain)

  model.fonts = fontsToUse
  model.fontStyles = fontStyles

  setAccessTokenCookie(context, await user.sign({ domain }))

  const data = parseQuery({ ...context.query })
  delete data.access_token

  await context.render('internal-board-service-view', { id, model, data, base })
})

// for board headless by name
standaloneBoardServiceRouter.get('/headless-by-name/:name', async (context, next) => {
  const { domain, user } = context.state

  if (!(await User.hasPrivilege('query', 'board', domain, user))) {
    context.status = 403
    return
  }

  const { name } = context.params

  const { model, base, board } = await headlessModel({ domain, name })
  const [fontsToUse, fontStyles] = await fonts(domain)

  model.fonts = fontsToUse
  model.fontStyles = fontStyles

  setAccessTokenCookie(context, await user.sign({ domain }))

  const data = parseQuery({ ...context.query })
  delete data.access_token

  await context.render('internal-board-service-view', { id: board?.id, model, data, base })
})

// for board player
standaloneBoardServiceRouter.get('/headless-player/:id', async (context, next) => {
  const { domain, user } = context.state

  if (!(await User.hasPrivilege('query', 'board', domain, user))) {
    context.status = 403
    return
  }

  const { id } = context.params

  const { playGroup, base } = await headlessPlaylist({ domain, id })
  const [fontsToUse, fontStyles] = await fonts(domain)

  setAccessTokenCookie(context, await user.sign({ domain }))

  const data = parseQuery({ ...context.query })
  delete data.access_token

  await context.render('internal-board-player-view', {
    id,
    model: {
      boards: playGroup?.boards,
      fonts: fontsToUse,
      fontStyles: fontStyles
    },
    data,
    base
  })
})

// for board player by name
standaloneBoardServiceRouter.get('/headless-player-by-name/:name', async (context, next) => {
  const { domain, user } = context.state

  if (!(await User.hasPrivilege('query', 'board', domain, user))) {
    context.status = 403
    return
  }

  const { name } = context.params

  const { playGroup, base } = await headlessPlaylist({ domain, name })
  const [fontsToUse, fontStyles] = await fonts(domain)

  setAccessTokenCookie(context, await user.sign({ domain }))

  const data = parseQuery({ ...context.query })
  delete data.access_token

  await context.render('internal-board-player-view', {
    id: playGroup?.id,
    model: {
      boards: playGroup?.boards,
      fonts: fontsToUse,
      fontStyles: fontStyles
    },
    data,
    base
  })
})

// for board thumbnail
standaloneBoardServiceRouter.get('/thumbnail/:id', async (context, next) => {
  const { domain, user } = context.state

  if (!(await User.hasPrivilege('query', 'board', domain, user))) {
    context.status = 403
    return
  }

  const { id } = context.params

  const { name, thumbnail } = (await getRepository(Board).findOneBy({ domain: { id: domain.id }, id })) || {}

  if (thumbnail) {
    const index = thumbnail.indexOf(';base64,')
    const base64 = thumbnail.substring(index + 8)
    const buffer = Buffer.from(base64, 'base64')

    context.type = 'image/png'
    context.set('Content-Disposition', contentDisposition(`thumbnail-${name}.png`))
    context.body = buffer
  } else {
    context.throw(404, 'thumbnail not found')
  }
})

// for board-template thumbnail
standaloneBoardServiceRouter.get('/board-template-thumbnail/:id', async (context, next) => {
  const { domain, user } = context.state

  if (!(await User.hasPrivilege('query', 'board-template', domain, user))) {
    context.status = 403
    return
  }

  const { id } = context.params

  const { name, thumbnail } = (await getRepository(BoardTemplate).findOneBy({ domain: { id: domain.id }, id })) || {}

  if (thumbnail) {
    const index = thumbnail.indexOf(';base64,')
    const base64 = thumbnail.substring(index + 8)
    const buffer = Buffer.from(base64, 'base64')

    context.type = 'image/png'
    context.set('Content-Disposition', contentDisposition(`thumbnail-${name}.png`))
    context.body = buffer
  } else {
    context.throw(404, 'thumbnail not found')
  }
})

// for webpage scrap
standaloneBoardServiceRouter.get('/screenshot/:id', async (context, next) => {
  const { domain, user } = context.state

  if (!(await User.hasPrivilege('query', 'board', domain, user))) {
    context.status = 403
    return
  }

  const { id } = context.params

  const { model, board } = await headlessModel({ domain, id })

  const data = parseQuery({ ...context.query })
  delete data.access_token

  context.type = 'image/png'
  context.set('Content-Disposition', contentDisposition(`${board.name}.png`))
  context.body = await screenshot({ id, model, data, options: { encoding: 'binary', type: 'png' }, context })
})

// for webpage scrap
standaloneBoardServiceRouter.get('/board/pdf/:id', async (context, next) => {
  const { domain, user } = context.state

  if (!(await User.hasPrivilege('query', 'board', domain, user))) {
    context.status = 403
    return
  }

  const { id } = context.params

  const { model, board } = await headlessModel({ domain, id })

  const data = parseQuery({ ...context.query })
  delete data.access_token

  const result = await pdf({
    id,
    model,
    data,
    options: {
      format: 'A4'
    },
    context
  })

  context.type = 'application/pdf'
  context.set('Content-Disposition', contentDisposition(`${board.name}.pdf`))
  context.body = result
})

// for webpage scrap => zpl image print(grf format) command for released version board
standaloneBoardServiceRouter.get('/label-command/:id', async (context, next) => {
  const { id } = context.params

  const data = context.query
  delete data.access_token

  const orientation = data && data.orientation
  const mirror = data && data.mirror
  const upsideDown = data && data.upsideDown

  context.type = 'text/plain'
  context.body = await labelcommand({ id, data, orientation, mirror, upsideDown, context } as any)
})

// for webpage scrap => zpl image print(grf format) command for draft version board
standaloneBoardServiceRouter.get('/label-command-draft/:id', async (context, next) => {
  const { id } = context.params

  const data = context.query
  delete data.access_token

  const orientation = data && data.orientation
  const mirror = data && data.mirror
  const upsideDown = data && data.upsideDown

  context.type = 'text/plain'
  context.body = await labelcommand({ id, data, orientation, mirror, upsideDown, context, draft: true } as any)
})
